import { DashboardCustomize } from "@/components/features/dashboard/DashboardCustomize";
import { CustomDashboardTabs } from "@/components/features/dashboard/CustomDashboardTabs";
import { DashboardMyApproachingStatutesCard } from "@/components/ui/DashboardMyApproachingStatutesCard/DashboardMyApproachingStatutesCard";
import { DashboardTasksCard } from "@/components/ui/DashboardTasksCard/DashboardTasksCard";
import { DashboardNotificationsCard } from "@/components/ui/DashboardNotificationsCard/DashboardNotificationsCard";
import { DashboardMostRecentMattersCard } from "@/components/ui/DashboardMostRecentMattersCard/DashboardMostRecentMattersCard";
import { DashboardPinnedMattersCard } from "@/components/ui/DashboardPinnedMattersCard/DashboardPinnedMattersCard";
import {
  ActionEmptyState,
  AppPageShell,
  Button,
  GlobalNotAuthorized,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutDashboard } from "lucide-react";
import { useCallback, useState } from "react";

export const Route = createFileRoute("/_auth/")({ component: RouteComponent });

function RouteComponent() {
  const { currentUser, hasPagePermission } = useUser();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedDashboardTabId, setSelectedDashboardTabId] =
    useState("overview");
  const selectDashboardTab = useCallback((tabId: string) => {
    setSelectedDashboardTabId(tabId);
    setCustomizeOpen(false);
  }, []);

  if (!hasPagePermission(PAGE_PERMISSION_KEYS.dashboard)) {
    return <GlobalNotAuthorized />;
  }

  // Get dashboard preferences from currentUser
  const showNotifications = Boolean(currentUser.dashboardNotifications);
  const showTasks = Boolean(currentUser.dashboardTasks);
  const showMyApproachingStatutes = Boolean(
    currentUser.dashboardMyApproachingStatuteOfLimitationMatters,
  );
  const showMostRecentMatters = Boolean(currentUser.dashboardMostRecentMatters);
  const showFavoriteMatters = Boolean(currentUser.dashboardFavoriteMatters);
  const canViewApproachingStatutes = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsApproachingStatutes,
  );
  const hasVisibleDashboardCard =
    showNotifications ||
    showTasks ||
    (showMyApproachingStatutes && canViewApproachingStatutes) ||
    showMostRecentMatters ||
    showFavoriteMatters;
  const hasCustomDashboard =
    currentUser.owner && hasPagePermission(PAGE_PERMISSION_KEYS.mattersAccess);

  const overview = hasVisibleDashboardCard ? (
    <div
      className="grid h-fit w-full auto-rows-auto grid-cols-2 gap-6"
      data-testid="dashboard-page"
    >
      {showNotifications && (
        <div className="col-span-2 lg:col-span-2">
          <DashboardNotificationsCard />
        </div>
      )}
      {showTasks && (
        <div className="col-span-2 lg:col-span-2">
          <DashboardTasksCard />
        </div>
      )}
      {showMyApproachingStatutes && canViewApproachingStatutes && (
        <div className="col-span-2 lg:col-span-2">
          <DashboardMyApproachingStatutesCard />
        </div>
      )}
      {showMostRecentMatters && (
        <div className="col-span-2 lg:col-span-2">
          <DashboardMostRecentMattersCard />
        </div>
      )}
      {showFavoriteMatters && (
        <div className="col-span-2 lg:col-span-2">
          <DashboardPinnedMattersCard />
        </div>
      )}
    </div>
  ) : (
    <div
      className="flex flex-1 items-center justify-center"
      data-testid="dashboard-page"
    >
      <ActionEmptyState
        icon={<LayoutDashboard className="h-5 w-5" />}
        title="Your dashboard is empty"
        description="Choose which sections you want to see on your dashboard."
        actions={
          <Button
            onClick={() => {
              setCustomizeOpen(true);
            }}
          >
            Customize
          </Button>
        }
      />
    </div>
  );

  return (
    <AppPageShell
      className={
        hasCustomDashboard && selectedDashboardTabId !== "overview"
          ? "h-full min-h-0 overflow-hidden"
          : undefined
      }
    >
      <div className="mb-6 flex min-h-9 shrink-0 items-center justify-between gap-4">
        <Text as="h1" variant="title">
          Dashboard
        </Text>
        {(!hasCustomDashboard || selectedDashboardTabId === "overview") && (
          <DashboardCustomize
            open={customizeOpen}
            onOpenChange={setCustomizeOpen}
          />
        )}
      </div>
      {hasCustomDashboard ? (
        <CustomDashboardTabs
          key={`${currentUser.firmId}:${currentUser.id}`}
          overview={overview}
          selectedTabId={selectedDashboardTabId}
          onSelectedTabIdChange={selectDashboardTab}
        />
      ) : (
        overview
      )}
    </AppPageShell>
  );
}
